import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut as firebaseSignOut
} from 'firebase/auth';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
let app = null;
let messaging = null;
let auth = null;

// Initialize Firebase App (core initialization)
const initializeFirebaseApp = () => {
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  return app;
};

// ============================================================================
// EAGER INITIALIZATION — settle IndexedDB before first auth operation
// ============================================================================
// Firebase lazily opens IndexedDB for heartbeat storage the first time an
// auth header is computed. On a fresh install, that means the FIRST call to
// signInWithPhoneNumber() races with IndexedDB's versionchange transaction
// (which creates the object stores). The race throws:
//
//   "Failed to execute 'transaction' on 'IDBDatabase':
//    The database connection is closing."
//
// and the OTP send fails with app/idb-get or app/idb-set. The second attempt
// always works because the DB was created (despite the error) on attempt 1.
//
// Fix: eagerly call initializeFirebaseApp() + getAuth() at module load so
// the heartbeat DB is created during page load — well before the user clicks
// Send OTP (typically 2-10 seconds later). The try/catch swallows any init
// error (e.g., missing env vars during SSR or tests).
try {
  const _earlyApp = initializeFirebaseApp();
  getAuth(_earlyApp);
} catch (_) {
  // Swallow — warm-up only; real errors surface in the auth flow later.
}

// Initialize Firebase Auth
const initializeAuth = () => {
  if (!auth) {
    const firebaseApp = initializeFirebaseApp();
    auth = getAuth(firebaseApp);
    // Set language to browser language
    auth.useDeviceLanguage();
  }
  return auth;
};

// Initialize Firebase Messaging (for push notifications)
const initializeFirebase = async () => {
  try {
    // Check if Firebase Messaging is supported in this browser
    const supported = await isSupported();

    if (!supported) {
      console.warn('Firebase Messaging is not supported in this browser');
      return null;
    }

    initializeFirebaseApp();

    if (!messaging) {
      messaging = getMessaging(app);
    }

    return messaging;
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    return null;
  }
};

// Get FCM token for push notifications
const getFCMToken = async () => {
  try {
    const messagingInstance = await initializeFirebase();

    if (!messagingInstance) {
      return null;
    }

    // Request notification permission
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      console.warn('Notification permission denied');
      return null;
    }

    // Get FCM token with VAPID key
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

    if (!vapidKey) {
      console.error('VAPID key not configured');
      return null;
    }

    // Firebase getToken() will automatically register the service worker at /firebase-messaging-sw.js
    // No need to wait for serviceWorker.ready - let Firebase handle it
    console.log('🔔 Firebase: Getting FCM token (Firebase will handle service worker registration)...');

    // Add timeout to getToken to prevent hanging
    const tokenPromise = getToken(messagingInstance, { vapidKey });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('FCM getToken timeout after 10 seconds')), 10000)
    );

    const token = await Promise.race([tokenPromise, timeoutPromise]);
    console.log('FCM Token obtained:', token ? 'Success' : 'Failed');

    return token;
  } catch (error) {
    console.error('Failed to get FCM token:', error);
    return null;
  }
};

// Listen for foreground messages
const onForegroundMessage = (callback) => {
  if (!messaging) {
    initializeFirebase().then((messagingInstance) => {
      if (messagingInstance) {
        onMessage(messagingInstance, callback);
      }
    });
  } else {
    onMessage(messaging, callback);
  }
};

// ============================================
// Firebase Phone OTP Authentication Functions
// ============================================

// Store recaptcha verifier instance
let recaptchaVerifier = null;

/**
 * Initialize reCAPTCHA verifier for phone authentication
 * @param {string} containerId - ID of the HTML element to render reCAPTCHA
 * @param {object} options - Optional configuration { size: 'invisible' | 'normal' }
 */
const initRecaptcha = (containerId, options = {}) => {
  try {
    const authInstance = initializeAuth();

    // Verify container element exists in DOM
    const container = document.getElementById(containerId);
    if (!container) {
      console.warn(`reCAPTCHA container '${containerId}' not found in DOM, skipping initialization`);
      return null;
    }

    // Reuse existing verifier if it's already set up. Creating/destroying
    // the verifier multiple times registers new reCAPTCHA instances with
    // Google, which tanks the trust score and triggers visible challenges.
    // Only clear + recreate when explicitly asked (e.g., clearRecaptcha
    // called from component unmount).
    if (recaptchaVerifier) {
      return recaptchaVerifier;
    }

    recaptchaVerifier = new RecaptchaVerifier(authInstance, containerId, {
      size: options.size || 'invisible',
      callback: () => {
        // reCAPTCHA solved - will proceed with phone verification
        console.log('reCAPTCHA verified');
      },
      'expired-callback': () => {
        // Response expired - user needs to solve again
        console.log('reCAPTCHA expired');
      }
    });

    return recaptchaVerifier;
  } catch (error) {
    console.error('Failed to initialize reCAPTCHA:', error);
    throw error;
  }
};

/**
 * Send OTP to phone number
 * @param {string} phoneNumber - Phone number in E.164 format (e.g., +919876543210)
 * @returns {Promise<ConfirmationResult>} - Firebase confirmation result for OTP verification
 */
const sendOTP = async (phoneNumber, _retryAttempt = 0) => {
  try {
    const authInstance = initializeAuth();

    if (!recaptchaVerifier) {
      throw new Error('reCAPTCHA not initialized. Call initRecaptcha first.');
    }

    // Ensure phone number has country code
    let formattedPhone = phoneNumber.trim();
    if (!formattedPhone.startsWith('+')) {
      // Default to India country code
      formattedPhone = '+91' + formattedPhone.replace(/^0+/, '');
    }

    const confirmationResult = await signInWithPhoneNumber(
      authInstance,
      formattedPhone,
      recaptchaVerifier
    );

    return confirmationResult;
  } catch (error) {
    // IndexedDB race on first load: Firebase heartbeat storage may still be
    // in a versionchange transaction when this runs. The error contains
    // "idb-get" or "idb-set" or "database connection is closing". It's
    // transient — a 1-second pause lets the DB settle, then retry succeeds.
    const isIdbRace =
      _retryAttempt === 0 &&
      error?.message &&
      (error.message.includes('idb-') ||
       error.message.includes('database connection is closing') ||
       error.message.includes('IndexedDB'));

    if (isIdbRace) {
      console.warn('IndexedDB not ready (first-load race), retrying in 1s...');
      await new Promise(r => setTimeout(r, 1000));
      return sendOTP(phoneNumber, 1);
    }

    console.error('Failed to send OTP:', error);

    // Reset reCAPTCHA on error for retry
    if (recaptchaVerifier) {
      try {
        await recaptchaVerifier.render();
      } catch (renderError) {
        console.warn('Could not reset reCAPTCHA:', renderError);
      }
    }

    throw error;
  }
};

/**
 * Verify OTP code
 * @param {ConfirmationResult} confirmationResult - Result from sendOTP
 * @param {string} otpCode - 6-digit OTP code entered by user
 * @returns {Promise<UserCredential>} - Firebase user credential
 */
const verifyOTP = async (confirmationResult, otpCode) => {
  try {
    if (!confirmationResult) {
      throw new Error('Invalid confirmation result. Please request OTP again.');
    }

    const userCredential = await confirmationResult.confirm(otpCode);
    return userCredential;
  } catch (error) {
    console.error('Failed to verify OTP:', error);
    throw error;
  }
};

/**
 * Get Firebase ID token for backend verification
 * @returns {Promise<string|null>} - Firebase ID token or null
 */
const getFirebaseIdToken = async () => {
  try {
    const authInstance = initializeAuth();
    const currentUser = authInstance.currentUser;

    if (!currentUser) {
      return null;
    }

    // Force refresh to get a fresh token
    const idToken = await currentUser.getIdToken(true);
    return idToken;
  } catch (error) {
    console.error('Failed to get Firebase ID token:', error);
    return null;
  }
};

/**
 * Sign out from Firebase Auth
 */
const signOutFirebase = async () => {
  try {
    const authInstance = initializeAuth();
    await firebaseSignOut(authInstance);
  } catch (error) {
    console.error('Failed to sign out from Firebase:', error);
    throw error;
  }
};

/**
 * Get current Firebase Auth user
 * @returns {User|null} - Firebase user or null
 */
const getCurrentFirebaseUser = () => {
  const authInstance = initializeAuth();
  return authInstance.currentUser;
};

/**
 * Clear reCAPTCHA verifier (call when unmounting component)
 */
const clearRecaptcha = () => {
  if (recaptchaVerifier) {
    // Delay the clear slightly so Google's reCAPTCHA internal deferred
    // callbacks (setTimeout in recaptcha__en.js) finish their DOM
    // animation/cleanup before we yank the verifier and its container.
    // Without this delay, reCAPTCHA's internal script throws:
    //   TypeError: Cannot read properties of null (reading 'style')
    // because it tries to access the container element that React already
    // removed from the DOM. The error is cosmetic (auth already succeeded)
    // but triggers the ErrorBoundary crash screen.
    const verifierToClean = recaptchaVerifier;
    recaptchaVerifier = null;
    setTimeout(() => {
      try { verifierToClean.clear(); } catch (_) {}
    }, 2000);
  }
};

export {
  // Push notifications (existing)
  initializeFirebase,
  getFCMToken,
  onForegroundMessage,
  // Phone OTP Authentication (new)
  initializeAuth,
  initRecaptcha,
  sendOTP,
  verifyOTP,
  getFirebaseIdToken,
  signOutFirebase,
  getCurrentFirebaseUser,
  clearRecaptcha
};
